import { usePatientEpisodesOfCareOverview } from '@ctw/shared/api/fhir/episode-of-care';
import type { EpisodeOfCareModel } from '@ctw/shared/api/fhir/models/episode-of-care';
import { applyFilters, type OverviewFilter } from '@ctw/shared/content/overview/filters';
import { datePredicate } from '@ctw/shared/content/resource/view-date-range';
import { applySorts } from '@ctw/shared/utils/sort';

export const getEpisodeOfCareOverviewFilters = (): Array<OverviewFilter<EpisodeOfCareModel>> => [
  {
    key: 'emergency',
    label: 'Filter for emergency episodes',
    description: 'Is an emergency or a hospitalization.',
    predicate: (episodeOfCare: EpisodeOfCareModel) =>
      ['EMER', 'IMP', 'OBSENC', 'PRENC'].includes(episodeOfCare.classCode),
  },
  {
    key: 'recent',
    label: 'Filter for recent episodes',
    description: 'Began within the past year.',
    predicate: (episodeOfCare: EpisodeOfCareModel) =>
      datePredicate(episodeOfCare, 'periodStart', 365, false),
  },
];

export interface EpisodeOfCareOverviewData {
  data: Array<EpisodeOfCareModel>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

/**
 * Fetches, sorts (newest first), and filters (emergency/inpatient within the
 * past year) the episodes of care shown in the patient overview card.
 * `hasNoRecords` reflects the raw fetch so callers can distinguish "no records"
 * from "none matching the emergency/inpatient past-year filter".
 */
export function useEpisodeOfCareOverviewData(): EpisodeOfCareOverviewData {
  const episodesOfCareQuery = usePatientEpisodesOfCareOverview();
  const sortedData = applySorts(episodesOfCareQuery.data ?? [], [
    { key: 'periodStart', dir: 'desc', isDate: true }, // Newest first.
  ]);
  const filteredData = applyFilters(sortedData, getEpisodeOfCareOverviewFilters());

  return {
    data: filteredData,
    isLoading: episodesOfCareQuery.isLoading,
    hasNoRecords: (episodesOfCareQuery.data?.length ?? 0) === 0,
  };
}
